/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Card, Checkbox, Col, DatePicker, Empty, Form, Input, Row, Select, Space, Tooltip } from 'antd';
import RichTextEditor from './Fields/RichTextEditor/RichTextEditor';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS, BROADCAST_TYPES, DEFAULT_DATETIME_FORMAT } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchGroupsQuery } from '../../store/groups/groupsApi';
import { useCreateBroadcastMutation, useUpdateBroadcastMutation } from '../../store/broadcasts/broadcastsApi';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../constants/markdown';
import is from '../../utils/is';
import getSupportedPlatforms from '../../utils/misc/getSupportedPlatforms';

// Constant declarations
const { Option } = Select;

const BROADCAST_SUPPORTED_RICH_TEXT_ACTIONS = [
  RICH_TEXT_TOOLBAR_ACTIONS.BOLD,
  RICH_TEXT_TOOLBAR_ACTIONS.ITALIC,
  RICH_TEXT_TOOLBAR_ACTIONS.EMOJI,
  RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST,
  RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST,
  RICH_TEXT_TOOLBAR_ACTIONS.LINK
];

const MAX_CHARS_FOR_BROADCAST_TEXT = 2000;

const BroadcastForm = ({
  canEdit = false,
  initialBroadcast,
  onSubmitSuccess = () => {},
  onCancel = () => {},
  bodyClassName,
  footerClassName,
  ...restProps
}) => {
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();

  const { data: botDeployments } = getCurrentQueryState('fetchBotDeployments', { orgId, tenantId });
  const { data: groupsData, isFetching: isLoadingGroups, isSuccess: isFetchGroupsSuccess } = useFetchGroupsQuery({ orgId, tenantId });
  const [createBroadcast, { isLoading: isCreatingBroadcast }] = useCreateBroadcastMutation();
  const [updateBroadcast, { isLoading: isUpdatingBroadcast }] = useUpdateBroadcastMutation();

  const [broadcastType, setBroadcastType] = useState(form ? form.getFieldValue('type') : null);
  const [platform, setPlatform] = useState(form ? form.getFieldValue('platform') : null);
  const [platforms, setPlatforms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [, updateState] = useState(); // Hook to force a component re-render

  useEffect(() => {
    setPlatforms(getSupportedPlatforms(botDeployments));
  }, [botDeployments]);

  useEffect(() => {
    if (platform === BOT_DEPLOYMENTS_CHANNELS.SLACK.key && broadcastType !== BROADCAST_TYPES.MESSAGE) {
      form.resetFields(['type']);
      setBroadcastType(null);
    }
  }, [platform]);

  useEffect(() => {
    if (isFetchGroupsSuccess) {
      setGroups(groupsData.items);
    }
  }, [isFetchGroupsSuccess]);

  // When there's a new broadcast to be edited
  useEffect(() => {
    form.resetFields();
    setBroadcastType(initialBroadcast?.type);
    setPlatform(initialBroadcast?.platform);
  }, [initialBroadcast]);

  const onSubmit = async (values) => {
    // Construct the submit values
    const valuesToSubmit = {
      ...values,
      sendAt: values.sendAt && values.sendAt.utc().format(),

      // Hardcoded defaults
      method: 'BATCHES',
      status: 'PENDING',
      label: 'Broadcast'
    };

    if (initialBroadcast) {
      valuesToSubmit['groups'] = valuesToSubmit.groups.map((group) => {
        return { id: group.id };
      });
    } else {
      valuesToSubmit['groups'] = valuesToSubmit.groups.map((group) => {
        return { id: group };
      });
    }

    let isSaveSuccessful = false;
    if (canEdit && initialBroadcast) {
      // Update existing broadcast
      isSaveSuccessful = await updateBroadcast({ orgId, tenantId, broadcastId: initialBroadcast.id, data: valuesToSubmit }).unwrap();
    } else {
      // Create new broadcast
      isSaveSuccessful = await createBroadcast({ orgId, tenantId, data: valuesToSubmit }).unwrap();

      if (isSaveSuccessful) {
        // Reset the form
        form.resetFields();
      }
    }

    if (isSaveSuccessful) {
      // Trigger callback to notify parent component of success
      onSubmitSuccess();
    }
  };

  const renderNoGroupsMessage = () => {
    return (
      <Empty
        description={
          <Row gutter={16} className="mt-3">
            <Col md={{ span: 12, offset: 6 }}>
              {t('There are no audiences available for scheduling a broadcast.')}
            </Col>
          </Row>
        }
        className="my-6"
      />
    );
  };

  const renderForm = () => {
    return (
      <Fragment>
        <div className={bodyClassName}>
          <Form
            name="broadcastForm"
            layout="vertical"
            initialValues={initialBroadcast ? {
              ...initialBroadcast,
              sendAt: moment(initialBroadcast.sendAt),
              groupIds: initialBroadcast.groups ? groups.filter(group => initialBroadcast.groups.some(initialBroadcastGroup => initialBroadcastGroup.id === group.id)).map(obj => obj.id) : []
            } : {
              type: Object.values(BROADCAST_TYPES).find(([_key, value]) => value === BROADCAST_TYPES.MESSAGE),
              sendAt: moment().add(5, 'minutes')
            }}
            onFinish={onSubmit}
            form={form}
            onValuesChange={(_, allFields) => {
              updateState(allFields);
            }}
            {...restProps}
          >
            <Row gutter={16}>
              <Col sm={12}>
                <Form.Item
                  name="platform"
                  label={t('Channel')}
                  rules={[{
                    required: true,
                    message: `${t('Please select a channel')}.`
                  }]}
                >
                  <Select
                    disabled={!canEdit}
                    onChange={(value) => {
                      setPlatform(value);

                      // Reset the audience selection
                      form.setFieldsValue({ groups: [] });
                    }}
                    placeholder={t('Select a channel')}
                    size="large"
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                  >
                    {
                      platforms?.map((platform) => {
                        return (
                          <Select.Option key={platform} value={platform}>
                            <Space size={4}>
                              {BOT_DEPLOYMENTS_CHANNELS[platform].icon} {t(platform, { context: 'platform type' })}
                            </Space>
                          </Select.Option>
                        );
                      })
                    }
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col sm={12}>
                <Form.Item
                  name="type"
                  label={t('Broadcast Type')}
                  rules={[{
                    required: true,
                    message: `${t('Please select a broadcast type')}.`
                  }]}
                >
                  <Select
                    disabled={!canEdit}
                    onChange={(value) => {
                      setBroadcastType(value);
                    }}
                    placeholder={t('Select broadcast type')}
                    size="large"
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                  >
                    {
                      // Slack only supports message broadcasts
                      platform === BOT_DEPLOYMENTS_CHANNELS.SLACK.key ? (
                        <Option key={BROADCAST_TYPES.MESSAGE} value={BROADCAST_TYPES.MESSAGE}>
                          {t(BROADCAST_TYPES.MESSAGE, { context: 'broadcast type' })}
                        </Option>
                      ) : (
                        Object.values(BROADCAST_TYPES).map(value => (
                          <Option key={value} value={value}>{t(value, { context: 'broadcast type' })}</Option>
                        ))
                      )
                    }
                  </Select>
                </Form.Item>
              </Col>

              <Col sm={12}>
                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{
                    required: true,
                    message: t('Please enter a title.')
                  },
                  {
                    min: 1,
                    max: 200,
                    message: t('Title must be between 1 and 200 characters.')
                  }]}
                >
                  <Input
                    disabled={!canEdit}
                    placeholder={t('Enter broadcast title')}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col sm={24}>
                <Form.Item
                  name="text"
                  label={t('Text')}
                  rules={[{
                    required: true,
                    message: t('Please enter the broadcast content.')
                  },
                  {
                    validator: async (rule, value) => {
                      try {
                        if (value && value.length > MAX_CHARS_FOR_BROADCAST_TEXT) {
                          throw new Error(rule.message);
                        }
                      } catch {
                        throw new Error(rule.message);
                      }
                    },
                    message: t('Exceeded character limit')
                  }
                  ]}
                >
                  <RichTextEditor
                    disabled={!canEdit}
                    placeholder={`${t('Enter broadcast content here')}...`}
                    shouldAutoFocus={false}
                    toolbarActions={BROADCAST_SUPPORTED_RICH_TEXT_ACTIONS}
                    showCount
                    maxLength={MAX_CHARS_FOR_BROADCAST_TEXT}
                    style={{ maxHeight: 300 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {
              // Conditional fields
              broadcastType === BROADCAST_TYPES.POLL || broadcastType === BROADCAST_TYPES.QUIZ ? (
                <Row gutter={16}>
                  <Col sm={12}>
                    <Card className="mb-4" title={t('Choices')}>
                      <Row gutter={16}>
                        <Col sm={24}>
                          <Form.List
                            name="choices"
                            rules={[
                              {
                                validator: async (_, choices) => {
                                  if (!choices) {
                                    return Promise.reject(new Error(`${t('Please add at least one choice')}.`));
                                  }
                                  if (broadcastType === BROADCAST_TYPES.QUIZ) {
                                    if (choices.length > 3) {
                                      return Promise.reject(new Error(`${t('You can only add up to three choices')}.`));
                                    }
                                    if (!choices.find((choice) => choice && choice.isCorrect)) {
                                      return Promise.reject(new Error(`${t('You must select one correct answer')}.`));
                                    }

                                    if (choices.filter(item => item.isCorrect).length > 1) {
                                      return Promise.reject(new Error(`${t('You can only select one correct answer')}.`));
                                    }
                                  }
                                }
                              }
                            ]}
                          >
                            {(fields, { add, remove }, { errors }) => (
                              <Fragment>
                                {fields.map(({ key, name, ...restField }, index) => (
                                  <Form.Item
                                    required={false}
                                    key={key}
                                  >
                                    <Row gutter={16}>
                                      <Col flex="auto">
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'text']}
                                          validateTrigger={['onChange', 'onBlur']}
                                          rules={[
                                            {
                                              required: true,
                                              whitespace: true,
                                              message: `${t('Please enter the choice text or delete this field')}.`
                                            }
                                          ]}
                                          noStyle
                                        >
                                          <Input
                                            disabled={!canEdit}
                                            placeholder={t('Choice text')}
                                            size="large"
                                          />
                                        </Form.Item>
                                      </Col>

                                      {
                                        fields.length > 1 ? (
                                          <Col flex="none">
                                            <Button
                                              disabled={!canEdit}
                                              type="default"
                                              size="large"
                                              icon={<DeleteIcon />}
                                              shape="circle"
                                              onClick={() => remove(name)}
                                            />
                                          </Col>
                                        ) : null
                                      }
                                    </Row>

                                    <Row gutter={16} className="mt-2">
                                      <Col sm={24}>
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'isCorrect']}
                                          hidden={broadcastType !== BROADCAST_TYPES.QUIZ}
                                          validateTrigger={['onChange', 'onBlur']}
                                          noStyle
                                          valuePropName="checked"
                                        >
                                          <Checkbox
                                            disabled={!canEdit}
                                          >
                                            {t('Correct answer')}
                                          </Checkbox>
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                  </Form.Item>
                                ))}

                                <Button
                                  type="dashed"
                                  size="large"
                                  block
                                  onClick={() => add({ text: '', isCorrect: false })}
                                  icon={<PlusIcon />}
                                  disabled={!canEdit}
                                >
                                  {t('Add new choice')}
                                </Button>
                                <Form.ErrorList errors={errors} />
                              </Fragment>
                            )}
                          </Form.List>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              ) : null
            }

            <Row gutter={16}>
              <Col sm={12}>
                <Form.Item
                  name="sendAt"
                  label={t('Send Broadcast at')}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        // If no value has been entered
                        if (!value) {
                          return Promise.reject(`${t('Please select a date to schedule the broadcast')}.`);
                        }

                        // If the user entered a date before our current date
                        if (moment(getFieldValue('sendAt')) < moment()) {
                          return Promise.reject(new Error(`${t('Please select a future date')}.`));
                        }

                        // Else
                        return Promise.resolve();
                      }
                    })
                  ]}
                >
                  <DatePicker
                    disabled={!canEdit}
                    format={DEFAULT_DATETIME_FORMAT}
                    disabledDate={(current) => {
                      return current && current < moment().startOf('day');
                    }}
                    showTime
                    size="large"
                    className="w-100"
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                  />
                </Form.Item>
              </Col>

              <Col sm={12}>
                <Form.Item
                  name="groups"
                  label={t('Audiences')}
                  rules={[{
                    required: true,
                    message: `${t('Please select at least one audience')}.`
                  }]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder={t('Select one or more audiences')}
                    size="large"
                    disabled={isLoadingGroups || !canEdit || !platform}
                    filterOption={(input, option) =>
                      groups && is.array(groups) && groups.find(group => group.id === option.value).label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                  >
                    {
                      groups && is.array(groups) ? (
                        groups.filter(group => group.platform === platform).map(group => (
                          <Option key={group.id} value={group.id}>{group.label}</Option>
                        ))
                      ) : null
                    }
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>

        <div className={footerClassName}>
          <Row justify="end" gutter={8}>
            <Col>
              <Button
                size="large"
                onClick={onCancel}
                disabled={!canEdit}
              >
                {t('Cancel')}
              </Button>
            </Col>

            <Col>
              <Tooltip
                title={
                  !form.getFieldValue('platform')
                    ? t('No valid channels are selected.')
                    : !form.getFieldValue('groups') || form.getFieldValue('groups').length === 0
                      ? t('No audiences are selected.')
                      : ''
                }
              >
                <Button
                  className="d-block ml-auto"
                  type="primary"
                  onClick={() => form.submit()}
                  loading={isCreatingBroadcast || isUpdatingBroadcast}
                  size="large"
                  disabled={!canEdit || !form.getFieldValue('platform') || !form.getFieldValue('groups') || form.getFieldValue('groups').length === 0}
                >
                  {t('Schedule Broadcast')}
                </Button>
              </Tooltip>
            </Col>
          </Row>
        </div>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {
        groups && is.array(groups) && !is.emptyArray(groups)
          ? renderForm()
          : renderNoGroupsMessage()
      }
    </Fragment>
  );
};

BroadcastForm.propTypes = {
  canEdit: PropTypes.bool,
  initialBroadcast: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default BroadcastForm;
